import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

import { Chip } from "ui/chip";
import { useOpenableState } from "libs/use-openable-state";
import { useClickOutside } from "libs/use-click-outside";
import { Collapse } from "ui/collapse";

import { Button, FormSize, FormThemeProvider } from "ui/ecosystems/forms";

// Custom event to coordinate filter popups — when one opens, others close
const FILTER_OPEN_EVENT = "selected-filter-open";

interface Props<T> {
  value: T;
  renderEditing(): React.ReactNode;
  onChange(value: T): void;
  onCancel?(): void;
  isValueEmpty?(value: T): boolean;
  onRemove?(): void;
  label: React.ReactNode;
  className?: string;
  hasValue?: boolean;
  icon?: string;
  autoCloseOnChange?: boolean;
}

function SelectedFilterComponent<T>(props: Props<T>) {
  const editing = useOpenableState(false);
  const instanceId = useRef(Math.random().toString(36).slice(2));
  const valueAtOpenRef = useRef<T | undefined>(undefined);
  const hasValueSnapshotRef = useRef(false);

  // Track whether the editor has been opened at least once
  const hasBeenOpenedRef = useRef(false);

  // Apply changes when closing the editor.
  // For auto-apply filters, avoid duplicate apply on close.
  // For other filters, apply on close and remove empty optional filters.
  function handleChange() {
    const isEmpty = props.isValueEmpty?.(props.value) ?? !props.value;
    const valueChangedSinceOpen =
      hasValueSnapshotRef.current && props.value !== valueAtOpenRef.current;

    if (props.autoCloseOnChange) {
      if (valueChangedSinceOpen) {
        return;
      }

      if (isEmpty && props.onRemove && hasBeenOpenedRef.current) {
        props.onRemove();
      }
      return;
    }

    if (isEmpty && props.onRemove && hasBeenOpenedRef.current) {
      props.onRemove();
      return;
    }

    // Avoid duplicate submits when the popup is closed without an actual edit.
    if (!valueChangedSinceOpen) return;

    props.onChange(props.value);
  }

  // Auto-open the editor on mount if the value is empty (user just added this filter)
  // Skip for permanent filters (no onRemove) — they're always visible and shouldn't auto-open
  useEffect(() => {
    if (!props.onRemove) return;
    const isEmpty = props.isValueEmpty?.(props.value) ?? !props.value;
    if (isEmpty) {
      editing.open();
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for other filters opening and close this one
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail !== instanceId.current && editing.isOpened) {
        props.onCancel?.();
        editing.close();
      }
    };
    document.addEventListener(FILTER_OPEN_EVENT, handler);
    return () => document.removeEventListener(FILTER_OPEN_EVENT, handler);
  }, [editing.isOpened]); // eslint-disable-line react-hooks/exhaustive-deps

  // Broadcast when this filter opens
  useEffect(() => {
    if (editing.isOpened) {
      hasBeenOpenedRef.current = true;
      // Capture the initial value once per open cycle so we can detect real edits.
      if (!hasValueSnapshotRef.current) {
        valueAtOpenRef.current = props.value;
        hasValueSnapshotRef.current = true;
        document.dispatchEvent(
          new CustomEvent(FILTER_OPEN_EVENT, { detail: instanceId.current })
        );
      }
    }

    if (!editing.isOpened && hasBeenOpenedRef.current) {
      handleChange();
      hasValueSnapshotRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing.isOpened, props.value]);

  // Auto-close when value changes if autoCloseOnChange is enabled
  useEffect(() => {
    const valueChangedSinceOpen =
      hasValueSnapshotRef.current && props.value !== valueAtOpenRef.current;

    if (
      props.autoCloseOnChange &&
      props.hasValue &&
      editing.isOpened &&
      valueChangedSinceOpen
    ) {
      const timer = setTimeout(() => {
        editing.close();
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [props.value, props.autoCloseOnChange, props.hasValue, editing]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    editing.close();
  }

  const popupRef = useRef(null);
  useClickOutside(popupRef, (event: MouseEvent) => {
    // Don't close if clicking on a Select dropdown portal (Radix UI portals to document.body)
    const target = event.target as HTMLElement;
    if (
      target.closest('[role="listbox"]') ||
      target.closest("[data-radix-popper-content-wrapper]")
    ) {
      return;
    }
    // Don't close if clicking on the chip itself (the chip's onClick handles toggling)
    const chipEl = popupRef.current
      ? (popupRef.current as HTMLElement).parentElement?.querySelector(
          "[data-filter-chip]"
        )
      : null;
    if (chipEl && chipEl.contains(target)) {
      return;
    }
    props.onCancel?.();
    editing.close();
  });

  function handleChipClick() {
    if (editing.isOpened) {
      props.onCancel?.();
      editing.close();
      return;
    }

    editing.open();
  }

  return (
    <div className={cn("relative", props.className)}>
      <Chip
        onClick={handleChipClick}
        onRemove={props.onRemove}
        isEditing={editing.isOpened}
        hasValue={props.hasValue}
        icon={props.icon}
        data-filter-chip
        className="max-w-[250px] whitespace-nowrap overflow-hidden text-ellipsis"
      >
        {props.label}
      </Chip>

      <div
        data-testid="filterEditPopup"
        ref={popupRef}
        className="absolute top-[calc(100%+8px)] z-10"
      >
        <Collapse isOpened={editing.isOpened}>
          <FormThemeProvider size={FormSize.medium}>
            <form
              onSubmit={handleSubmit}
              className="min-w-[280px] p-4 bg-white border border-neutral-200 rounded-lg shadow-md animate-in slide-in-from-top-2 duration-200"
            >
              <div className="relative">
                {props.renderEditing()}
                <div className="flex justify-center mt-3">
                  <Button
                    data-testid="filterDoneButton"
                    type="button"
                    onClick={editing.close}
                    size={FormSize.small}
                    className="h-8 px-4 text-sm text-blue-500 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-pointer"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </form>
          </FormThemeProvider>
        </Collapse>
      </div>
    </div>
  );
}

export const SelectedFilter = SelectedFilterComponent;
